import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import DOMPurify from 'dompurify';
/* eslint-disable no-useless-escape */

export const isValid = {
    email: (value: string) => String(value)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ),
    url: (value: string) => String(value).match(/^((https?|ftp|smtp):\/\/)?(www\.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/)
};

export const googleLibrary: any = {
    oauth2: '',
    callback: ''
};

export const getID = () => uuidv4();

export const formatTimeAgo = (timeAgoText: string, timeAgo: string) => {
    const postDate = new Date(timeAgo);
    const now = new Date();
    
    // Calculate precise date differences using calendar units
    const yearsDiff = now.getFullYear() - postDate.getFullYear();
    const monthsDiff = now.getMonth() - postDate.getMonth();
    const daysDiff = now.getDate() - postDate.getDate();
    
    // Calculate total months difference
    let totalMonths = yearsDiff * 12 + monthsDiff;
    if (daysDiff < 0) {
        totalMonths -= 1; // If we haven't reached the same day of month yet
    }
    
    // Calculate fallback time units for smaller differences
    const diffMs = now.getTime() - postDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    
    let timeText = timeAgoText; // Fallback to original text
    
    // Determine the most appropriate time unit
    if (totalMonths >= 12) {
        const years = Math.floor(totalMonths / 12);
        timeText = years === 1 ? '1 year ago' : `${years} years ago`;
    } else if (totalMonths > 0) {
        timeText = totalMonths === 1 ? '1 month ago' : `${totalMonths} months ago`;
    } else if (diffWeeks > 0) {
        timeText = diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
    } else if (diffDays > 0) {
        timeText = diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else if (diffHours > 0) {
        timeText = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffMinutes > 0) {
        timeText = diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    } else {
        timeText = 'Just now';
    }

    const formattedDate = postDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    return `${timeText} (${formattedDate})`;
};

// Text helpers
export const normalizeMessageForTextarea = (text: string): string => {
    if (!text) return '';
    let result = text;
    // Convert escaped newlines ("\\n") to real newlines
    result = result.replace(/\\n/g, '\n');
    // If HTML-like content, convert common tags to newlines and strip others
    if (/<[a-z][\s\S]*>/i.test(result)) {
        result = result
            .replace(/<br\s*\/?>(gi)/gi, '\n')
            .replace(/<\/p>\s*<p>/gi, '\n')
            .replace(/<\/div>\s*<div>/gi, '\n')
            .replace(/<li>/gi, '• ')
            .replace(/<\/li>/gi, '\n')
            .replace(/<\/?(p|div|ul|ol)>/gi, '')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>');
    }
    return result;
};

export const replacePlaceholders = (text: string, replacements: Record<string, string | undefined>): string => {
    if (!text) return ''
    let result = text
    // Replace every {{key}} placeholder (case-insensitive, with optional whitespace) with the provided value
    for (const [key, value] of Object.entries(replacements)) {
        const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}}`, 'gi')
        result = result.replace(regex, value || '')
    }
    return result
}

export const autosizeTextarea = (el: HTMLTextAreaElement, maxHeight: number = 120, resetWhenEmpty: boolean = true) => {
    if (!el) return
    const hasContent = (el.value || '').length > 0
    if (!hasContent && resetWhenEmpty) {
        el.style.removeProperty('height')
        return
    }
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px'
}

export const convertEmoticonsToEmoji = (text: string): string => {
    if (!text) return text as string;
    let result = text;
    const replacements: Array<[RegExp, string]> = [
        [/(:-\)|:\))/g, '🙂'],
        [/(:-\(|:\()/g, '🙁'],
        [/(;-\)|;\))/g, '😉'],
        [/(:-D|:D)/g, '😄'],
        [/(:-P|:P)/gi, '😛'],
        [/(:-O|:O)/gi, '😮'],
        [/(<3)/g, '❤️'],
    ];
    for (const [regex, emoji] of replacements) {
        result = result.replace(regex, emoji);
    }
    return result;
}

export const analytic = (body: any) => {
    const local = window.location.host.includes('localhost');

    if (!local) {
        try {
            (window as any).dataLayer = (window as any).dataLayer || [];

            (window as any).dataLayer.push({
                ...body
            });

            console.log('Analytic', body);
        }
        catch (error) {
            console.log('Analytic error', error);
        }
    }
    else {
        console.log('Analytic', body);
    }
};

export const getPageUrl = () => `${window.location.host}${window.location.pathname}`;

export const getPageTitle = () => {
    if (window.location.pathname === '/') return 'Billing';

    if (window.location.pathname.includes('/processing')) return 'Processing';

    if (window.location.pathname.includes('/subscription')) return 'Subscription';

    if (window.location.pathname.includes('/paymentMethods')) return 'Payment methods';

    if (window.location.pathname.includes('/updateSubscription')) return 'Update subscription';

    if (window.location.pathname.includes('/info')) return 'Info';

    if (window.location.pathname.includes('/signup')) return 'Sign up';

    if (window.location.pathname.includes('/integrations')) return 'Integrations/MCP';

    if (window.location.pathname.includes('/redirect')) return 'Redirect';
};

export const calculateColumnWidth = (offsetWidth: number | undefined, columns: any, isSmall: boolean = false, isMedium: boolean = false) => {
    if (offsetWidth) {
        // Filter columns based on isSmall or isMedium flags
        const filteredColumns = columns.filter((column: any) => {
            if (isSmall && column.showSmall && column.show) return true;
            if (isMedium && (column.showSmall || column.showMedium) && column.show) return true;
            if (!isSmall && !isMedium && column.show) return true;
            return false;
        });

        const fixedWidths: number = filteredColumns.reduce((total: number, column: any) => {
            if (column.show) {
                if (typeof column.width === 'string' && column.width.endsWith('px')) {
                    return total + parseInt(column.width); // Parse width from string to number
                }
                return total + (column.width || 0); // Use as number directly if already a number
            }
            return total;
        }, 0);

        // Calculate the number of columns without a fixed width but with show: true
        const variableColumns = filteredColumns.filter((column: any) => !column.width && column.show);
        const variableColumnsCount: number = variableColumns.length;

        // Calculate the width for each variable column
        let variableWidth: number;
        if (variableColumnsCount > 0) {
            variableWidth = (offsetWidth - fixedWidths) / variableColumnsCount;
        } else {
            variableWidth = 0;
        }

        // Assign the calculated width to each variable column
        const adjustedColumns = columns.map((column: any) => {
            if (column.show) {
                // Only apply width calculations to filtered columns
                if (filteredColumns.includes(column)) {
                    if (!column.width) {
                        return { ...column, width: `${variableWidth}px` }; // Convert variableWidth to string with 'px' suffix
                    } else if (typeof column.width === 'string' && column.width.endsWith('px')) {
                        return { ...column, width: column.width }; // Keep specified width as string
                    } else {
                        return { ...column, width: column.width }; // Keep specified width as number
                    }
                }
            }
            return column; // Return the column as is if show is not true
        });

        return adjustedColumns;
    }
}

export const adjustedColumnsHandler = (columnsInitialState: any, columns: any, tableRef: any, isSmall: boolean, isMedium: boolean, sortHandler: any = false, useLocalStorageSetup: boolean = false) => {
    // Fetch the localStorage setup only if the flag is true
    const localStorageColumnsSetup = useLocalStorageSetup ? localStorage.getItem('columns-setup') : null
    const columnsSetup = localStorageColumnsSetup ? JSON.parse(localStorageColumnsSetup) : {}

    const widthMapping = columnsInitialState.reduce((acc: any, column: any) => {
        acc[column.key[0]] = column.width
        return acc
    }, {} as Record<string, string | undefined>)

    const mappedColumnsWithShow = columns.map((column: any) => {
        
        // Get the initial width if it exists
        const initialWidth = widthMapping[column.key[0]];

        if (useLocalStorageSetup && columnsSetup.hasOwnProperty(column.key[0])) {
            return {
                ...column,
                show: columnsSetup[column.key[0]],
                width: initialWidth, // Apply the initial width or remove it if undefined
            }
        }

        // Return the column without width if it wasn't initially sets
        return {
            ...column,
            ...sortHandler && {
                sortHandler: column?.sortHandler
                ? (e: any) => sortHandler(e)
                : column?.sortHandler
            },
            width: initialWidth // Apply the initial width or remove it if undefined
        };
    });
    
    const offsetWidth = tableRef.current?.offsetWidth
    const adjustedColumns = calculateColumnWidth(offsetWidth, mappedColumnsWithShow, isSmall, isMedium)

    return adjustedColumns
}

export const sanitize = (value: string | string[] | undefined): string => {
    if (!value) return '';
    const sanitizedValue = Array.isArray(value) ? value.join('') : value;
    return DOMPurify.sanitize(sanitizedValue)
}

export const capitalLetters = (props: any, fullName = false) => {
    let firstName = ''
    let lastName = ''

    if (props?.firstName !== undefined) {
        firstName = fullName
            ? props.firstName
            : (props.firstName != null ? props.firstName.charAt(0).toUpperCase() : '')
    }
    if (props?.lastName !== undefined) {
        lastName = fullName
            ? props.lastName
            : (props.lastName != null ? props.lastName.charAt(0).toUpperCase() : '')
    }

    return fullName ? firstName + ' ' + lastName : firstName + lastName
}

export const userVersionToString = (value: string) => {
    if (value === 'in-1st') return 'Linkedin 1st degree'; 

    if (value === 'in-2nd') return 'Linkedin 2nd degree'; 

    if (value === 'imported') return 'Imported'; 

    if (value === 'in-prospect') return 'imported via AI prospector'; 

    return ''; 
};

export const isLNFirst = (connection: any, userId: any) => {
    return connection?.autoTagsArray?.some((tagArrayItem: any) => tagArrayItem?._id === userId &&
        tagArrayItem?.autoTags?.some(
            (tag: any) => tag?.title === "LinkedIn 1st" || tag?.title === "New Connection"
        )
    )
}

//Pipes

export const formatNumber = (value: number) => {
    return value.toLocaleString();
}

export const formatPrice = (value: number) => {
    return (value / 100).toLocaleString(undefined, {minimumFractionDigits: 2})
}

export const formatPriceTwoDecimals = (value: number) => {
    return (value / 100).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

/** Splits a cents amount into locale-formatted integer and fraction parts (works with comma or dot decimals). */
export const splitFormattedPriceTwoDecimals = (valueInCents: number) => {
    const amount = valueInCents / 100
    const parts = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).formatToParts(amount)

    let integerPart = ''
    let decimalPart = '00'

    for (const part of parts) {
        if (part.type === 'integer' || part.type === 'group') {
            integerPart += part.value
        } else if (part.type === 'fraction') {
            decimalPart = part.value
        }
    }

    return { integerPart, decimalPart }
}

export const formatRegularPriceTwoDecimals = (value: number) => {
    return value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

export const formatCurrency = (value: string) => {
    if(value === 'usd') return "$";
    return "€";
}

export const oldBusinessPrices = () => {
    return ['price_1PRDPuHNTUwAGNhQfiullIPA', 'price_1Phnz4HNTUwAGNhQ5NDY5L2W', 
        'price_1PhoDCHNTUwAGNhQPBbiQ8P8', 'price_1PREKuHNTUwAGNhQtc55eagM',
        'price_1PoSyHHNTUwAGNhQZt8Z4z6y', 'price_1PoSzdHNTUwAGNhQZW7c6lcM',
        'price_1PoTALHNTUwAGNhQPL2xpoxG', 'price_1PoTALHNTUwAGNhQylsNq8NK'
        ];
}

export const oldProPrices = () => {
    return ['price_1PRDMLHNTUwAGNhQb4qfIUd6', 'price_1PRDJkHNTUwAGNhQDZo0DTTc', 
        'price_1Pho5tHNTUwAGNhQqoVwtSeU', 'price_1Pho4wHNTUwAGNhQaJA9sesj',
        'price_1PoSrtHNTUwAGNhQ55JcB78h', 'price_1PoStwHNTUwAGNhQNCwXBK69',
        'price_1PoT5gHNTUwAGNhQxA15QCBt', 'price_1PoT5gHNTUwAGNhQXTukC5BY'
        ];
}

export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/** Read email from query string without treating + as a space (plus-addressing). */
export const getEmailFromQueryString = (search: string): string | null => {
    const match = search.match(/[?&]email=([^&]*)/i);
    if (!match?.[1]) return null;
    try {
        return decodeURIComponent(match[1].replace(/\+/g, '%2B')).trim();
    } catch {
        return null;
    }
};

export const getOS = (): 'mac' | 'windows' | 'linux' | 'other' => {
    if (typeof navigator === 'undefined') return 'other';

    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.platform) {
        const platform = userAgentData.platform.toLowerCase();
        if (platform.includes('mac')) return 'mac';
        if (platform.includes('win')) return 'windows';
        if (platform.includes('linux')) return 'linux';
    }
  
    return 'other';
};

/** Mac-style keyboard (⌘). Uses getOS with userAgent fallback for older browsers. */
export const isMacLikeKeyboard = (): boolean => {
    if (typeof navigator === 'undefined') return false
    if (getOS() === 'mac') return true
    return /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent || '')
}

export type Browser = 'chrome' | 'edge' | 'opera' | 'brave' | 'firefox' | 'safari' | 'other'

const CHROME_BASED_BROWSERS: Browser[] = ['chrome', 'edge', 'opera', 'brave']

/** Detects the current browser. Chromium forks (Arc, Vivaldi, etc.) typically resolve to `chrome`. */
export const getBrowser = (): Browser => {
    if (typeof navigator === 'undefined') return 'other'

    const ua = navigator.userAgent || ''
    const brandNames = (((navigator as any).userAgentData?.brands ?? []) as Array<{ brand: string }>)
        .map(({ brand }) => brand)

    const hasBrand = (pattern: RegExp) => brandNames.some(brand => pattern.test(brand))

    // Legacy Edge (EdgeHTML) — not Chromium-based.
    if (/Edge\//i.test(ua) && !/Edg\//i.test(ua)) return 'other'

    if (hasBrand(/Microsoft Edge/i) || /Edg\//i.test(ua) || /EdgiOS/i.test(ua)) return 'edge'
    if (hasBrand(/^Opera$/i) || /OPR\//i.test(ua)) return 'opera'
    if (hasBrand(/^Brave$/i) || /Brave/i.test(ua)) return 'brave'
    if (hasBrand(/Google Chrome/i) || /Chrome|CriOS/i.test(ua)) return 'chrome'
    if (/Firefox|FxiOS/i.test(ua)) return 'firefox'
    if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR/i.test(ua)) return 'safari'

    if (hasBrand(/Chromium/i)) return 'chrome'

    return 'other'
}

/** Chromium-based browsers (Chrome, Edge, Opera, Brave, Arc, etc.). Not Safari or Firefox. */
export const isChromeBasedBrowser = (): boolean => CHROME_BASED_BROWSERS.includes(getBrowser())

/** Phone-sized mobile device via user agent (not viewport width). Tablets may return false. */
export const isMobileDevice = (): boolean => {
    if (typeof navigator === 'undefined') return false

    const userAgentData = (navigator as any).userAgentData
    if (typeof userAgentData?.mobile === 'boolean') {
        return userAgentData.mobile
    }

    const ua = navigator.userAgent || ''
    return /Android|iPhone|iPod|Mobile|IEMobile|Opera Mini/i.test(ua)
}

/**
 * Returns initials from the 'name' field (first letter of first and last name, or just the first if there is no last name)
 * @param obj Object with a 'name' field (string)
 * @returns string initials
 */
export const getNameInitials = (obj: { name?: string }) => {
    if (!obj?.name || typeof obj.name !== 'string') return '';
    const parts = obj.name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0).toUpperCase() || '';
    const last = parts[1]?.charAt(0).toUpperCase() || '';
    return first + last;
}

// Styled link component with hover underline effect
const StyledLink = styled.a<{ $linkColor: string }>`
    color: ${props => props.$linkColor};
    text-decoration: none;
    cursor: pointer;
    opacity: 1;
    
    &:hover {
        text-decoration: underline;
        opacity: 1;
        color: ${props => props.$linkColor};
    }
`;

/**
 * Converts URLs and email addresses in text to clickable links with primary600 color
 * @param text The text to parse
 * @param linkColor The color for links (defaults to primary600)
 * @returns JSX with text and clickable links
 */
export const parseLinksInText = (text: string, linkColor: string = '#5865f2') => {
    if (!text) return null;
    
    // Regex patterns for URLs and emails
    const urlPattern = /(?:https?:\/\/|www\.)[^\s<>"{}|\\^`[\]]+/gi;
    const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
    
    // Find all matches with their positions
    const matches: Array<{ start: number; end: number; text: string; type: 'url' | 'email' }> = [];
    
    let match: RegExpExecArray | null;
    while ((match = urlPattern.exec(text)) !== null) {
        matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            type: 'url'
        });
    }
    
    while ((match = emailPattern.exec(text)) !== null) {
        // Make sure this email is not part of a URL we already matched
        const isPartOfUrl = matches.some(m => 
            m.type === 'url' && match!.index >= m.start && match!.index < m.end
        );
        if (!isPartOfUrl) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                text: match[0],
                type: 'email'
            });
        }
    }
    
    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);
    
    // If no matches found, return the text as is
    if (matches.length === 0) {
        return text;
    }
    
    // Build result with links
    const result: Array<string | JSX.Element> = [];
    let lastIndex = 0;
    
    matches.forEach((match, index) => {
        // Add text before this match
        if (match.start > lastIndex) {
            result.push(text.substring(lastIndex, match.start));
        }
        
        // Add the link
        const href = match.type === 'email' 
            ? `mailto:${match.text}`
            : match.text.startsWith('http') ? match.text : `https://${match.text}`;
        
        result.push(
            <StyledLink
                key={`link-${index}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                $linkColor={linkColor}
            >
                {match.text}
            </StyledLink>
        );
        
        lastIndex = match.end;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
        result.push(text.substring(lastIndex));
    }
    
    return <>{result}</>;
}

export const transformProfilePosts = (lnPosts: any): any[] => {
    const posts: any[] = [];

    if (!lnPosts?.included) {
        return posts;
    }

    // Find all update objects in the included array
    const updates = lnPosts.included.filter((item: any) => 
        item.$type === "com.linkedin.voyager.dash.feed.Update"
    );

    // First pass: create all posts normally
    const allPosts: any[] = [];
    const postsById = new Map<string, any>();

    // Helper function to find video play metadata by entityUrn
    const findVideoPlayMetadata = (entityUrn: string) => {
        return lnPosts.included.find((item: any) => 
            item.$type === "com.linkedin.videocontent.VideoPlayMetadata" &&
            item.entityUrn === entityUrn
        );
    };

    const extractUgcPost = (socialDetail: string): { urn: string | null } => {
        if (!socialDetail) return { urn: null };
        
        // Try to extract ugcPost ID and URN first
        if (socialDetail.includes('ugcPost:')) {
            const match = socialDetail.match(/ugcPost:(\d+)/);
            if (match) {
                return {
                    urn: `urn:li:ugcPost:${match[1]}` 
                };
            }
        }
        
        // Try to extract activity ID and URN if no ugcPost found
        if (socialDetail.includes('activity:')) {
            const match = socialDetail.match(/activity:(\d+)/);
            if (match) {
                return { 
                    urn: `urn:li:activity:${match[1]}` 
                };
            }
        }
        
        return { urn: null };
    };

    updates.forEach((update: any, index: number) => {
        const ugcPostData = extractUgcPost(update["*socialDetail"]);
        const ugcPostId = ugcPostData.urn;
        const activityId = update.entityUrn?.split('activity:')?.[1]?.split(',')[0] || `post-${index}`;
        
        // Find social activity counts - match by multiple possible identifiers
        const socialActivity = lnPosts.included.find((item: any) => {
            if (item.$type !== "com.linkedin.voyager.dash.feed.SocialActivityCounts") {
                return false;
            }
            
            // Try to match by activityId in entityUrn or urn
            if (item.entityUrn?.includes(activityId) || item.urn?.includes(activityId)) {
                return true;
            }
            
            // Try to match by ugcPostId if available
            if (ugcPostId && (item.entityUrn?.includes(ugcPostId) || item.urn?.includes(ugcPostId))) {
                return true;
            }
            
            // Try to match by shareUrn from metadata
            const shareUrn = update.metadata?.shareUrn;
            if (shareUrn) {
                const shareId = shareUrn.split(':').pop();
                if (item.entityUrn?.includes(shareId) || item.urn?.includes(shareId)) {
                    return true;
                }
            }
            
            return false;
        });
        


        // Extract content from commentary
        const postContent = update.commentary?.text?.text || "";
        
        // Find share URL from socialContent or from update itself
        let shareUrl = "";
        if (update.socialContent?.shareUrl) {
            shareUrl = update.socialContent.shareUrl;
        } else {
            // Extract from the update's shareUrn if available
            const shareUrn = update.metadata?.shareUrn;
            if (shareUrn) {
                const shareId = shareUrn.split(':').pop();
                shareUrl = `https://www.linkedin.com/feed/update/urn:li:share:${shareId}`;
            }
        }
        
        // Determine post type based on content
        let post_type: "text" | "image" | "image_carousel" | "video" | "document" | "poll" | "article" | "celebration" = "text";
        let post_image: string | null = null;
        let post_images: string[] | null = null;
        let post_document: string | null = null;
        let post_video: string | null = null;
        let post_poll: string | null = null;
        let post_article: string | null = null;
        let article: any = null;
        let celebration: any = null;

        // Check for image content
        if (update.content?.imageComponent?.images?.[0]) {
            post_type = "image";
            
            // Check if there are multiple images
            const images = update.content.imageComponent.images;
            if (images.length > 1) {
                post_type = "image_carousel";
                
                // Extract all image URLs
                const imageUrls = images.map((imageComponent: any) => {
                    const vectorImage = imageComponent.attributes?.[0]?.detailData?.vectorImage;
                    
                    if (vectorImage?.rootUrl && vectorImage.artifacts?.length > 0) {
                        // Find the artifact with width 800 (or fallback to first available)
                        const artifact = vectorImage.artifacts.find((a: any) => a.width === 800) || 
                                       vectorImage.artifacts[0];
                        
                        if (artifact) {
                            return `${vectorImage.rootUrl}${artifact.fileIdentifyingUrlPathSegment}`;
                        }
                    }
                    return null;
                }).filter(Boolean);
                
                post_image = imageUrls[0]; // First image as main image
                post_images = imageUrls; // All images for carousel
            } else {
                // Single image
                const imageComponent = images[0];
                const vectorImage = imageComponent.attributes?.[0]?.detailData?.vectorImage;
                
                if (vectorImage?.rootUrl && vectorImage.artifacts?.length > 0) {
                    // Find the artifact with width 800 (or fallback to first available)
                    const artifact = vectorImage.artifacts.find((a: any) => a.width === 800) || 
                                   vectorImage.artifacts[0];
                    
                    if (artifact) {
                        post_image = `${vectorImage.rootUrl}${artifact.fileIdentifyingUrlPathSegment}`;
                    }
                }
            }
        } else if (update.content?.linkedInVideoComponent) {
            post_type = "video";
            
            // Get video play metadata entityUrn
            const videoEntityUrn = update.content.linkedInVideoComponent["*videoPlayMetadata"];
            
            if (videoEntityUrn) {
                // Find the actual video play metadata object
                const videoPlayMetadata = findVideoPlayMetadata(videoEntityUrn);
                
                if (videoPlayMetadata) {
                    // Try to get the highest quality video URL
                    if (videoPlayMetadata.progressiveStreams?.length > 0) {
                        // Find the highest quality progressive stream
                        const bestStream = videoPlayMetadata.progressiveStreams.reduce((prev: any, current: any) => {
                            return (prev.bitRate > current.bitRate) ? prev : current;
                        });
                        
                        if (bestStream?.streamingLocations?.length > 0) {
                            post_video = bestStream.streamingLocations[0].url;
                        }
                    } else if (videoPlayMetadata.adaptiveStreams?.length > 0) {
                        // Fallback to adaptive streams (HLS/DASH)
                        const adaptiveStream = videoPlayMetadata.adaptiveStreams[0];
                        if (adaptiveStream?.masterPlaylists?.length > 0) {
                            post_video = adaptiveStream.masterPlaylists[0].url;
                        }
                    }
                }
            }
        } else if (update.content?.documentComponent) {
            post_type = "document";
            const document = update.content.documentComponent.document;
            post_document = document?.transcribedDocumentUrl || document?.manifestUrl || null;
            
            if (document) {
                console.log("Document details:", {
                    title: document.title,
                    url: document.transcribedDocumentUrl,
                    pageCount: document.totalPageCount,
                    entityUrn: document.entityUrn
                });
            }
        } else if (update.content?.pollComponent) {
            post_type = "poll";

            let poll_question = update.content.pollComponent.question?.text || null;
          
            let poll_options: any[] = [];
            if (Array.isArray(update.content.pollComponent.pollOptions)) {
              poll_options = update.content.pollComponent.pollOptions.map((opt: any) => ({
                text: opt.option?.text || "",
                urn: opt.pollOptionUrn || "",
                preDashUrn: opt.preDashPollOptionUrn || ""
              }));
            }
            post_poll = `${update.content.pollComponent.entityUrn || null}#${poll_question}#${poll_options.map(opt => opt.text).join(',')}`;
          } else if (update.content?.articleComponent) {
            post_type = "article";
            const articleComponent = update.content.articleComponent;
            post_article = articleComponent.articleUrn || null;

            // Extract navigation context
            const actionTarget = articleComponent?.navigationContext?.actionTarget || null;
            const accessibilityText = articleComponent?.navigationContext?.accessibilityText || null;

            // Extract image artifact URLs (smallImage / largeImage)
            const images: any[] = [];
            const addVectorImageToImages = (vectorImage: any) => {
                if (vectorImage?.rootUrl && Array.isArray(vectorImage?.artifacts)) {
                    vectorImage.artifacts.forEach((a: any) => {
                        if (a?.fileIdentifyingUrlPathSegment) {
                            images.push({
                                width: a.width,
                                height: a.height,
                                url: `${vectorImage.rootUrl}${a.fileIdentifyingUrlPathSegment}`
                            });
                        }
                    });
                }
            };

            const smallVectorImage = articleComponent?.smallImage?.attributes?.[0]?.detailData?.vectorImage;
            const largeVectorImage = articleComponent?.largeImage?.attributes?.[0]?.detailData?.vectorImage;
            addVectorImageToImages(smallVectorImage);
            addVectorImageToImages(largeVectorImage);

            article = {
                urn: post_article,
                actionTarget,
                accessibilityText,
                images
            };
        } else if (update.content?.celebrationComponent) {
            post_type = "celebration";
            
            const celebrationComponent = update.content.celebrationComponent;
            
            // Extract celebration image
            let celebrationImage: string | null = null;
            if (celebrationComponent.image?.attributes?.[0]?.detailData?.imageUrl?.url) {
                celebrationImage = celebrationComponent.image.attributes[0].detailData.imageUrl.url;
            }
            
            // Extract celebration headline
            const headline = celebrationComponent.headline?.text || null;
            
            celebration = {
                image: celebrationImage,
                headline: headline
            };
        }

        // Extract metrics from social activity
        const num_likes = socialActivity?.numLikes || 0;
        const num_comments = socialActivity?.numComments || 0;
        const num_shares = socialActivity?.numShares || 0;
        const num_views = socialActivity?.numImpressions || 0;
        const reacted = socialActivity?.reacted || null;
        const total_engagements = num_likes + num_comments + num_shares;
        const engagement_rate = num_views > 0 
            ? ((total_engagements / num_views) * 100).toFixed(2) + "%" 
            : "Inf";

        // Extract date - try to get from createdAt or use metadata
        let createdAt = Date.now();
        if (update.metadata?.backendUrn) {
            // Try to extract date from the backendUrn or use current date
            createdAt = update.metadata.createdAt || Date.now();
        }
        
        const event_date = new Date(createdAt).toISOString();
        
        let time_ago_text = "";
        // Extract time from actor's subDescription text (format: "4w •", "2d •", etc.)
        const subDescriptionText = update.actor?.subDescription?.text || "";
        const timeMatch = subDescriptionText.match(/^(\d+[a-z]+)\s*•/);
        if (timeMatch) {
            time_ago_text = timeMatch[1].trim();
        } 

        const calculateApproximateDate = (timeAgoText: string) => {
            const now = new Date();
            
            // Parse LinkedIn's abbreviated format (e.g., "4w", "2d", "3h", "15m")
            const match = timeAgoText.match(/^(\d+)([a-z]+)$/);
            if (!match) return now.toISOString();
            
            const numValue = parseInt(match[1], 10);
            const unit = match[2];
            
            if (isNaN(numValue)) return now.toISOString();

            switch (unit) {
                case 'm': // minutes
                    now.setMinutes(now.getMinutes() - numValue);
                    break;
                case 'h': // hours
                    now.setHours(now.getHours() - numValue);
                    break;
                case 'd': // days
                    now.setDate(now.getDate() - numValue);
                    break;
                case 'w': // weeks
                    now.setDate(now.getDate() - (numValue * 7));
                    break;
                case 'mo': // months
                    now.setMonth(now.getMonth() - numValue);
                    break;
                case 'y': // years
                    now.setFullYear(now.getFullYear() - numValue);
                    break;
                case 'yr': // years (alternative format)
                    now.setFullYear(now.getFullYear() - numValue);
                    break;
                default:
                    return now.toISOString();
            }
            return now.toISOString();
        };

        const time_ago = calculateApproximateDate(time_ago_text);

        const extractHashtagsForActivity = (activityId: string): string[] => {
            return lnPosts.included
                .filter((item: any) =>
                    item.$type === "com.linkedin.voyager.dash.feed.Hashtag" &&
                    item.entityUrn?.includes(`urn:li:activity:${activityId}`)
                )
                .map((tag: any) => {
                    const match = tag.entityUrn?.match(/hashtag:\(([^,]+),/);
                    return match ? match[1] : null;
                })
                .filter(Boolean) as string[];
        };


        const isRepost = (update: any): boolean =>
            update.metadata?.rootShare === false ||
            update.entityUrn?.includes('RESHARED') ||
            !!update['*resharedUpdate'];
          
        const getOriginalAuthorPublicId = (update: any, included: any[]): string => {
                if (!update['*resharedUpdate']) return '';
                const orig = included.find(i => i.entityUrn === update['*resharedUpdate']);
                
                // 1) try direct publicIdentifier on the found update (for personal profiles)
                if (orig?.publicIdentifier) return orig.publicIdentifier;
              
                // 2) fallback: resolve via profile URN in included (for personal profiles)
                const profileUrn = orig?.actor?.name?.attributesV2?.[0]?.detailData?.['*profileFullName'];
                if (profileUrn) {
                  const profile = included.find(p => p.entityUrn === profileUrn);
                  if (profile?.publicIdentifier) return profile.publicIdentifier;
                }
                
                return '';
              };

        const getOriginalCompanyName = (update: any, included: any[]): string => {
                if (!update['*resharedUpdate']) return '';
                const orig = included.find(i => i.entityUrn === update['*resharedUpdate']);
                
                // for companies: resolve via company URN in included
                const companyUrn = orig?.actor?.name?.attributesV2?.[0]?.detailData?.['*companyName'];
                if (companyUrn) {
                  const company = included.find(c => c.entityUrn === companyUrn);
                  if (company?.name) return company.name;
                }
                
                return '';
              };

        const getOriginalCompanyLogo = (update: any, included: any[]): string | null => {
                if (!update['*resharedUpdate']) return null;
                const orig = included.find(i => i.entityUrn === update['*resharedUpdate']);
                
                // for companies: get logo from actor image
                const companyLogo = orig?.actor?.image?.attributes?.[0]?.detailData?.nonEntityCompanyLogo?.vectorImage;
                if (companyLogo?.rootUrl && companyLogo?.artifacts?.length > 0) {
                  // Find the artifact with width 200 (or fallback to first available)
                  const artifact = companyLogo.artifacts.find((a: any) => a.width === 200) || 
                                 companyLogo.artifacts[0];
                  
                  if (artifact) {
                    return `${companyLogo.rootUrl}${artifact.fileIdentifyingUrlPathSegment}`;
                  }
                }
                
                return null;
              };


        const repost = isRepost(update);
        const linked_in_user_person_id = repost ? getOriginalAuthorPublicId(update, lnPosts.included) : null;
        const linked_in_company_name = repost ? getOriginalCompanyName(update, lnPosts.included) : null;
        const linked_in_company_logo = repost ? getOriginalCompanyLogo(update, lnPosts.included) : null;

        const post_tags = extractHashtagsForActivity(activityId);

        const postObject = {
            id: activityId,
            event_date,
            event_type: "new_post",
            blob: {
                post_content: postContent,
                post_image,
                post_images,
                post_document,
                post_video,
                post_poll,
                post_article,
                is_newsletter: false,
                num_likes,
                num_comments,
                num_shares,
                num_views,
                permalink: shareUrl,
                reacted,
                ugcPostId,
                article,
                celebration,
            },
            total_engagements,
            engagement_rate,
            linked_in_user_person_id: linked_in_user_person_id,
            linked_in_company_name: linked_in_company_name,
            linked_in_company_logo: linked_in_company_logo,
            time_ago_text,
            time_ago,
            post_type,
            post_sub_type: repost ? "reshare" : "original",
            post_tags: post_tags
        };

        allPosts.push(postObject);
        postsById.set(activityId, postObject);
    });

    // Second pass: link reposts with originals and filter
    const repostedOriginalIds = new Set<string>();
    
    allPosts.forEach(post => {
        // Find the original update for this post to check if it's a repost
        const update = updates.find((u: any) => {
            const updateActivityId = u.entityUrn?.split('activity:')?.[1]?.split(',')[0];
            return updateActivityId === post.id;
        });
        
        if (update && update['*resharedUpdate']) {
            const originalActivityId = update['*resharedUpdate'].split('activity:')[1]?.split(',')[0];
            if (originalActivityId) {
                const originalPost = postsById.get(originalActivityId);
                if (originalPost) {
                    // Add the original post's blob as repost field
                    (post as any).reposted_post = originalPost;
                    // Track original post for removal
                    repostedOriginalIds.add(originalActivityId);
                }
            }
        }
    });

    // Filter out original posts that were reposted
    const finalPosts = allPosts.filter(post => !repostedOriginalIds.has(post.id));

    return finalPosts;
};

export interface JobTriple {
    firstName: string
    lastName: string
    position: string
    company: string
    profileId: string
}

const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// LinkedIn Catch up → Job changes is localized to the account language.
// getJobChanges does not send a locale, so each language needs its own phrase.
// Supported: EN, DE. Other languages are dropped and never reach POST /job-changes.
//
// SDUI splits the sentence: the quote ends at the preposition and the company
// is the next "strong" node. actorHeadline keeps the full sentence.
// EN: "Started a new position as {position} at" + strong {company}
// DE: "Hat eine neue Stelle als {position} bei" + strong {company}
//     actorHeadline: "... bei {company} angetreten"
// The congrats / Glückwunsch button is truncated and has no position, so it is not parsed.
// Alternative extraction logic for formatted files
const extractJobTriplesAlternative = (text: string): JobTriple[] => {
    const source = text
    const results: JobTriple[] = []

    const jobChangeRegex = /"(?:Started a new position as ([^"]+) at|Hat eine neue Stelle als ([^"]+) bei)"/g
    let jobMatch: RegExpExecArray | null
    
    while ((jobMatch = jobChangeRegex.exec(source)) !== null) {
        const position = (jobMatch[1] || jobMatch[2] || '').trim()
        const jobIndex = jobMatch.index
        
        // Search backwards for the name (within 500 characters)
        const backwardContext = source.slice(Math.max(0, jobIndex - 500), jobIndex)
        const nameMatch = /"children":\s*\[\s*"([^"]+\s+[^"]+)"\s*\]/g
        let fullName = ''
        let firstName = ''
        let lastName = ''
        
        // Get the last name match before job change text
        let lastNameMatch: RegExpExecArray | null = null
        let nm: RegExpExecArray | null
        while ((nm = nameMatch.exec(backwardContext)) !== null) {
            lastNameMatch = nm
        }
        
        if (lastNameMatch) {
            fullName = lastNameMatch[1]
            const nameParts = fullName.split(' ')
            if (nameParts.length >= 2) {
                firstName = nameParts[0]
                lastName = nameParts.slice(1).join(' ')
            }
        }
        
        // Search forward for company name in strong tag (within 500 characters)
        const forwardContext = source.slice(jobIndex, Math.min(source.length, jobIndex + 500))
        const companyMatch = /"strong"[^}]*"children":\s*\[\s*"([^"]+)"\s*\]/g.exec(forwardContext)
        const company = companyMatch ? companyMatch[1] : ''
        
        // Search for profileId in wider context
        let profileId = ''
        const widerContext = source.slice(Math.max(0, jobIndex - 20000), Math.min(source.length, jobIndex + 5000))
        const profileIdMatch = /"profileId":"(ACo[^"]+)"/g.exec(widerContext)
        if (profileIdMatch) {
            profileId = profileIdMatch[1]
        }
        
        results.push({
            firstName,
            lastName,
            position,
            company,
            profileId
        })
    }
    
    // Also look for "Congrats on starting your new role" pattern
    const congratsRegex = /"Congrats on starting your new role as ([^"]+) at"/g
    let congratsMatch: RegExpExecArray | null
    
    while ((congratsMatch = congratsRegex.exec(source)) !== null) {
        const position = congratsMatch[1].trim()
        const jobIndex = congratsMatch.index
        
        // Search backwards for the name
        const backwardContext = source.slice(Math.max(0, jobIndex - 500), jobIndex)
        const nameMatch = /"children":\s*\[\s*"([^"]+\s+[^"]+)"\s*\]/g
        let fullName = ''
        let firstName = ''
        let lastName = ''
        
        let lastNameMatch: RegExpExecArray | null = null
        let nm: RegExpExecArray | null
        while ((nm = nameMatch.exec(backwardContext)) !== null) {
            lastNameMatch = nm
        }
        
        if (lastNameMatch) {
            fullName = lastNameMatch[1]
            const nameParts = fullName.split(' ')
            if (nameParts.length >= 2) {
                firstName = nameParts[0]
                lastName = nameParts.slice(1).join(' ')
            }
        }
        
        // Search forward for company name
        const forwardContext = source.slice(jobIndex, Math.min(source.length, jobIndex + 500))
        const companyMatch = /"strong"[^}]*"children":\s*\[\s*"([^"]+)"\s*\]/g.exec(forwardContext)
        const company = companyMatch ? companyMatch[1] : ''
        
        // Search for profileId
        let profileId = ''
        const widerContext = source.slice(Math.max(0, jobIndex - 20000), Math.min(source.length, jobIndex + 5000))
        const profileIdMatch = /"profileId":"(ACo[^"]+)"/g.exec(widerContext)
        if (profileIdMatch) {
            profileId = profileIdMatch[1]
        }
        
        results.push({
            firstName,
            lastName,
            position,
            company,
            profileId
        })
    }

    return results
}

// Original extraction logic for unformatted files
const extractJobTriplesOriginal = (text: string): JobTriple[] => {
    const source = text
        .replace(/\\+\"/g, '"')
        .replace(/\\\//g, '/')

    const results: JobTriple[] = []

    const actorHeadlineRegex = /\"actorHeadline(?:\\)?\"\s*:\s*\"([^\"]+)\"/g
    // Full sentence. EN ends at company; DE company sits between "bei" and "angetreten". See note above.
    const parseRx = /(?:Started a new position(?:\s+as)?\s+(.+?)\s+(?:at|@)\s+([^\.\!\"\)]+)|Hat eine neue Stelle als\s+(.+?)\s+bei\s+(.+?)\s+angetreten)/i

    let mh: RegExpExecArray | null
    while ((mh = actorHeadlineRegex.exec(source)) !== null) {
        const head = mh[1]
        const m2 = parseRx.exec(head)
        if (!m2) continue

        const posIndex = mh.index
        const ctxStart = Math.max(0, posIndex - 15000)
        const ctxEnd = Math.min(source.length, posIndex + 15000)
        const ctx = source.slice(ctxStart, ctxEnd)

        // Prefer a small forward window after the headline for nearby fields
        const fastCtx = source.slice(posIndex, Math.min(source.length, posIndex + 2000))

        let ridMatch = /\"recipientNonIterableId\"\s*:\s*\"(ACo[^\"]+)\"/.exec(fastCtx)
        if (!ridMatch) ridMatch = /\"recipientNonIterableId\"\s*:\s*\"(ACo[^\"]+)\"/.exec(ctx)
        const profileId = ridMatch ? ridMatch[1] : ''

        let firstName = ''
        let lastName = ''

        if (profileId) {
            const pidRegex = new RegExp(`\"profileId\"\\s*:\\s*\"${escapeRegExp(profileId)}\"`, 'g')
            const candidates: number[] = []
            let mPid: RegExpExecArray | null
            while ((mPid = pidRegex.exec(source)) !== null) {
                candidates.push(mPid.index)
            }
            const windowSize = 15000
            for (const idx of candidates) {
                const wStart = Math.max(0, idx - windowSize)
                const wEnd = Math.min(source.length, idx + windowSize)
                const w = source.slice(wStart, wEnd)
                const fn = /\"firstName\"\s*:\s*\"([^\"]+)\"/.exec(w)
                const ln = /\"lastName\"\s*:\s*\"([^\"]+)\"/.exec(w)
                if (fn) firstName = firstName || fn[1]
                if (ln) lastName = lastName || ln[1]
                if (firstName && lastName) break
            }
        }

        results.push({
            firstName,
            lastName,
            position: (m2[1] || m2[3] || '').trim(),
            company: (m2[2] || m2[4] || '').trim().replace(/\\+$/g, ''),
            profileId
        })
    }

    return results
}

export const extractJobTriples = (text: string): JobTriple[] => {
    // First try alternative logic (for formatted files)
    const altResults = extractJobTriplesAlternative(text)
    
    if (altResults.length > 0) {
        console.log(`Alternative logic found ${altResults.length} results`)
        
        // Try to enrich with profileId from actor data
        const actorRegex = /"profileId":"(ACo[^"]+)"[^}]*"firstName":"([^"]+)"[^}]*"lastName":"([^"]+)"/g
        const actors: Array<{profileId: string, firstName: string, lastName: string}> = []
        let actorMatch: RegExpExecArray | null
        while ((actorMatch = actorRegex.exec(text)) !== null) {
            actors.push({
                profileId: actorMatch[1],
                firstName: actorMatch[2],
                lastName: actorMatch[3]
            })
        }
        
        // Match results with actors by name
        for (const result of altResults) {
            if (!result.profileId && result.firstName && result.lastName) {
                const matchingActor = actors.find(actor => 
                    actor.firstName === result.firstName && actor.lastName === result.lastName
                )
                if (matchingActor) {
                    result.profileId = matchingActor.profileId
                }
            }
        }
        
        // Remove duplicates
        const uniq: JobTriple[] = []
        const seen = new Set<string>()
        for (const r of altResults) {
            const key = [r.firstName, r.lastName, r.position, r.company, r.profileId].join('|')
            if (!seen.has(key)) { seen.add(key); uniq.push(r) }
        }
        
        return uniq
    }
    
    // Fallback to original logic
    console.log('Alternative logic found no results, trying original logic...')
    const originalResults = extractJobTriplesOriginal(text)
    console.log(`Original logic found ${originalResults.length} results`)
    
    // Remove duplicates
    const uniq: JobTriple[] = []
    const seen = new Set<string>()
    for (const r of originalResults) {
        const key = [r.firstName, r.lastName, r.position, r.company, r.profileId].join('|')
        if (!seen.has(key)) { seen.add(key); uniq.push(r) }
    }
    
    return uniq
}

// --- SDUI people search parsing ---

export interface ParsedPerson {
    fullName: string;
    publicIdentifier: string;
    profileId: string | null;
    profileUrl: string;
    photoUrl: string | null;
    profilePicture: string | null;
}

export const parsePeopleFromSdui = (
    sduiText: string,
    options?: { excludePublicIdentifier?: string }
): ParsedPerson[] => {
    if (!sduiText || typeof sduiText !== "string") return [];

    const library: Record<string, string> = {};
    const photoLibrary: Record<string, string> = {};

    // 1. STEP: Build the dictionary of text and images from the end of the file
    const textMatches = Array.from(sduiText.matchAll(/^([a-z0-9]+):\[.*?"children":\s*\[\s*"([^"\]]+)"/gm));
    for (const match of textMatches) {
        library[`$L${match[1]}`] = match[2];
    }

    // Catch JSON string value including escape (\", \\, \u0022) to avoid URL being shortened
    const jsonStringCapture = '((?:[^"\\\\]|\\\\.)*)';
    const imageBlocks = sduiText.split('\n').filter(line => line.includes('digitalmediaAsset'));
    imageBlocks.forEach(block => {
        const keyMatch = block.match(/^([a-z0-9]+):/);
        const rootMatch = block.match(new RegExp('"rootUrl":"' + jsonStringCapture + '"'));
        // Prefer 800x800, 400x400, 200x200, 100x100 (larger = full URL with token params)
        const suffixPat = '"suffixUrl":"' + jsonStringCapture + '"';
        let suffixMatch = block.match(new RegExp('"width":800[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":799[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":461[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":450[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":400[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":399[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":200[^}]*' + suffixPat)) ||
            block.match(new RegExp('"width":199[^}]*' + suffixPat)) ||
            block.match(new RegExp(suffixPat));
        if (!keyMatch || !rootMatch || !suffixMatch) return;

        let root = rootMatch[1].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');
        let suffix = suffixMatch[1].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');

        // Old format has two variants:
        // A) With B4DZ hash: suffix = "800_800/B4DZ.../0/NUM" – no duplicate to remove
        // B) Without hash: suffix = "800_800/profile-displayphoto-shrink_800_800/0/NUM" – keep full path;
        //    removing duplicate produces .../shrink_800_800/0/NUM which doesn't load.
        // Only remove duplicate when suffix has B4DZ-style hash; old format has "profile-displayphoto-shrink_" in suffix – keep full path
        const isOldShrinkFormat = suffix.includes("profile-displayphoto-shrink_");
        if (root.endsWith("profile-displayphoto-shrink_") && isOldShrinkFormat) {
            // Old format without hash: keep full path (don't remove duplicate)
        } else if (root.endsWith("profile-displayphoto-shrink_")) {
            suffix = suffix.replace(/\/profile-displayphoto-shrink_\d+_\d+\//, "/");
        }

        let fullUrl = root + suffix;
        // Keep original path: "scale_100_100" and "crop_800_800" are valid CDN paths – do NOT replace with shrink_
        photoLibrary[`$L${keyMatch[1]}`] = fullUrl;
    });

    // Ordered profile URLs from photoLibrary (digitalmediaAsset) – fallback when renderPayload list is short
    const orderedPhotoLibraryUrls = Object.values(photoLibrary).filter((u) =>
        u.includes('profile-displayphoto')
    );

    // Pre-extract ALL profile-displayphoto URLs from renderPayload ($Led components)
    // Use global regex (not split-by-line) – SDUI can be minified to a single line.
    const rpJson = '((?:[^"\\\\]|\\\\.)*)';
    const allRenderPayloadUrls: string[] = [];
    const rpRegex = new RegExp(
        '"renderPayload":\\{"rootUrl":"' + rpJson + '","imageRenditions":\\[\\{[^}]*"suffixUrl":"' + rpJson + '"',
        'g'
    );
    let rpMatch;
    while ((rpMatch = rpRegex.exec(sduiText)) !== null) {
        const root = rpMatch[1].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');
        if (!root.includes('profile-displayphoto')) continue;
        const suffix = rpMatch[2].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');
        let full = root + suffix;
        // Old format has "profile-displayphoto-shrink_" in suffix – keep full path; B4DZ format doesn't, replace is no-op
        const isOldShrinkFormat = suffix.includes("profile-displayphoto-shrink_");
        if (root.endsWith('profile-displayphoto-shrink_') && !isOldShrinkFormat) {
            full = full.replace(/\/profile-displayphoto-shrink_\d+_\d+\//, '/');
        }
        allRenderPayloadUrls.push(full);
    }

    // 2. Step: Merge profiles
    const people: ParsedPerson[] = [];
    const resultsBlocks = sduiText.split('people-search-result');

    resultsBlocks.slice(1).forEach((block, blockIndex) => {
        // We look for the public URL/Slug (e.g. https://www.linkedin.com/in/john-doe/)
        const urlMatch = block.match(/"https:\/\/www\.linkedin\.com\/in\/([a-zA-Z0-9%-_]+)\/?"/);
        if (!urlMatch) return;

        // Clean up publicIdentifier (remove the trailing slash) (e.g. john-doe)
        const publicIdentifier = decodeURIComponent(urlMatch[1]).replace(/\/$/, '');

        // We look for profileId (internal URN that starts with ACoA...) (e.g. ACoA1234567890)
        const profileIdMatch = block.match(/"profileId":"(ACoA[^"]+)"/) ||
            block.match(/"targetProfileUrn":"(ACoA[^"]+)"/) ||
            block.match(/"payload":\{"profileId":"(ACoA[^"]+)"/);
        const profileId = profileIdMatch ? profileIdMatch[1] : null;

        if (options?.excludePublicIdentifier && publicIdentifier === options.excludePublicIdentifier) return;

        // Catching Name via pointer (e.g. John Doe)
        const titleKeyMatch = block.match(/"viewName":"search-result-lockup-title".*?"children":"(\$L[a-z0-9]+)"/);
        let fullName = titleKeyMatch ? library[titleKeyMatch[1]] : null;

        // Catching Image via pointer or raw URL (e.g. https://media.licdn.com/dms/image/v2/...)
        // IMPORTANT: Do NOT use "image":"$Lxx" – that only appears in sortableImages (mutual
        // connection avatars), so it would show the same avatar for different people.
        const imageKeyMatch =
            block.match(/"direction":"horizontal","children":\["(\$L[a-z0-9]+)"/) ||
            block.match(/"children":\["(\$L[a-z0-9]+)",\["\$","div"/);
        let photoUrl = imageKeyMatch ? photoLibrary[imageKeyMatch[1]] : null;
        if (!photoUrl) {
            // Fallback: extract renderPayload (rootUrl + suffixUrl) directly from block when present
            // Use same capture as main parser to get full URL including ?e=&v=&t= token params
            const rpJson = '((?:[^"\\\\]|\\\\.)*)';
            const renderPayloadMatch = block.match(
                new RegExp('"renderPayload":\\{"rootUrl":"' + rpJson + '","imageRenditions":\\[\\{[^}]*"suffixUrl":"' + rpJson + '"')
            );
            if (renderPayloadMatch) {
                let root = renderPayloadMatch[1].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');
                let suffix = renderPayloadMatch[2].replace(/\\"/g, '"').replace(/\\u0026/g, '&').replace(/\\/g, '');
                const isOldShrinkFormat = suffix.includes("profile-displayphoto-shrink_");
                if (root.endsWith("profile-displayphoto-shrink_") && !isOldShrinkFormat) {
                    suffix = suffix.replace(/\/profile-displayphoto-shrink_\d+_\d+\//, "/");
                }
                let full = root + suffix;
                // Keep original path: scale_/crop_ are valid CDN paths
                photoUrl = full;
            }
        }
        if (!photoUrl) {
            const rawImg = block.match(/https:\/\/media\.licdn\.com\/dms\/image\/v2\/[^" ]+/);
            if (rawImg) photoUrl = rawImg[0].replace(/\\/g, '').replace(/u0026/g, '&');
        }
        // Fallback: use ordered profile images – renderPayload first, then photoLibrary.
        // Use people.length (not blockIndex) so index matches actual profile order when some blocks lack urlMatch.
        const peopleIndex = people.length;
        if (!photoUrl && peopleIndex < allRenderPayloadUrls.length) {
            photoUrl = allRenderPayloadUrls[peopleIndex];
        }
        if (!photoUrl && peopleIndex < orderedPhotoLibraryUrls.length) {
            photoUrl = orderedPhotoLibraryUrls[peopleIndex];
        }

        // Cleaning up the name (removing the connection degree) (e.g. John Doe)
        if (fullName) fullName = fullName.split(/[•\u00b7]/)[0].trim();
        else fullName = publicIdentifier.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

        if (!people.some(p => p.publicIdentifier === publicIdentifier)) {
            const pic = photoUrl || null;
            people.push({
                fullName: decodeURIComponent(fullName),
                publicIdentifier: publicIdentifier,
                profileId: profileId,
                profileUrl: `https://www.linkedin.com/in/${publicIdentifier}/`,
                photoUrl: pic,
                profilePicture: pic
            });
        }
    });

    return people;
};

export interface PaginationData {
    hasNextPage: boolean;
    totalPages: number;
    nextPageBindingKey: string | null;
}

export const getPaginationData = (sduiText: string): PaginationData => {
    if (!sduiText || typeof sduiText !== "string") {
        return { hasNextPage: false, totalPages: 0, nextPageBindingKey: null };
    }

    // 1. Total number of pages from SearchResultsPeopleindicatorCountVal
    let totalPages = 0;
    const totalRegex = /"SearchResultsPeopleindicatorCountVal"[^]*?"intValue"\s*:\s*(\d+)/;
    const totalMatch = sduiText.match(totalRegex);
    if (totalMatch) {
        totalPages = parseInt(totalMatch[1], 10);
    }

    // 2. Binding key for next page (SearchResultsauto-binding-...)
    let nextPageBindingKey: string | null = null;
    const bindingMatch = sduiText.match(/"(SearchResultsauto-binding-[a-f0-9-]+)"/);
    if (bindingMatch) {
        nextPageBindingKey = bindingMatch[1];
    }

    // 3. There is a next page if we have a binding key and there is more than one page
    const hasNextPage = nextPageBindingKey !== null && totalPages > 1;

    return {
        hasNextPage,
        totalPages,
        nextPageBindingKey,
    };
};

export interface ParsedMutualConnection {
    rank: number;
    entityResultUrn: string | null;
    profileUrn: string | null;
    profileId: string | null;
    trackingUrn: string | null;
    fullName: string | null;
    firstName: string | null;
    lastName: string | null;
    publicIdentifier: string | null;
    linkedinUrl: string | null;
    headline: string | null;
    location: string | null;
    connectionDegreeText: string | null;
    mutualInsight: string | null;
    mutualConnectionsCount: number | null;
    mutualProfileLinks: string[];
    mutualConnectionsSearchUrl: string | null;
    imageUrl: string | null;
    rawEntityResult: any;
    rawProfile: any;
}

export interface ParsedMutualConnectionsResult {
    connections: ParsedMutualConnection[];
    paging: {
        start: number;
        count: number;
        total: number;
    } | null;
    metadata: {
        searchId: string | null;
        primaryResultType: string | null;
        totalResultCount: number;
        paginationToken: string | null;
    } | null;
    hasMore: boolean;
    nextStart: number | null;
    debug: {
        hasSearchRoot: boolean;
        clustersCount: number;
        includedCount: number;
        entityResultRefsCount: number;
        inferredEntityResultsCount?: number;
    };
}

export const parseMutualConnectionsPaging = (payload: any) => {
    const searchRoot = payload?.data?.data?.searchDashClustersByAll
        || payload?.data?.searchDashClustersByAll
        || payload?.searchDashClustersByAll
        || null;

    if (!searchRoot) {
        return {
            start: 0,
            count: 0,
            total: 0,
            hasMore: false,
            nextStart: null,
            paginationToken: null,
        };
    }

    const paging = searchRoot?.paging || {};
    const start = Number.isFinite(Number(paging?.start)) ? Number(paging.start) : 0;
    const count = Number.isFinite(Number(paging?.count)) ? Number(paging.count) : 0;
    const total = Number.isFinite(Number(paging?.total))
        ? Number(paging.total)
        : Number(searchRoot?.metadata?.totalResultCount || 0);
    const nextStart = (start + count) < total ? (start + count) : null;

    return {
        start,
        count,
        total,
        hasMore: nextStart !== null,
        nextStart,
        paginationToken: searchRoot?.metadata?.paginationToken || null,
    };
};

export const parseMutualConnectionsSearchResponse = (payload: any): ParsedMutualConnectionsResult => {
    const unwrapPayload = (input: any) => {
        let current = input;
        for (let i = 0; i < 8; i += 1) {
            if (current == null) break;

            if (typeof current === "string") {
                try {
                    current = JSON.parse(current);
                    continue;
                } catch (_e) {
                    break;
                }
            }

            if (typeof current !== "object") break;

            // Common extension wrappers.
            if (current?.result != null) {
                current = current.result;
                continue;
            }
            if (typeof current?.text === "string") {
                current = current.text;
                continue;
            }
            if (typeof current?.raw === "string") {
                current = current.raw;
                continue;
            }
            if (typeof current?.data === "string") {
                current = current.data;
                continue;
            }

            break;
        }
        return current;
    };

    const normalizedPayload = unwrapPayload(payload);

    const getText = (value: any) => {
        if (!value || typeof value !== "object") return null;
        return typeof value.text === "string" ? value.text : null;
    };

    const parseProfileUrnFromEntityResultRef = (entityResultRef: any) => {
        if (typeof entityResultRef !== "string") return null;
        const match = entityResultRef.match(/\(urn:li:fsd_profile:([^,]+),/);
        if (!match || !match[1]) return null;
        return `urn:li:fsd_profile:${match[1]}`;
    };

    const parseProfileIdFromUrn = (profileUrn: string | null) => {
        if (typeof profileUrn !== "string") return null;
        const parts = profileUrn.split(":");
        return parts.length ? parts[parts.length - 1] : null;
    };

    const parsePublicIdentifierFromUrl = (url: any) => {
        if (typeof url !== "string") return null;
        const match = url.match(/linkedin\.com\/in\/([^?\/]+)/i);
        if (!match || !match[1]) return null;
        return decodeURIComponent(match[1]);
    };

    const extractImageUrl = (entityResult: any) => {
        const attributes = entityResult?.image?.attributes;
        if (!Array.isArray(attributes)) return null;

        for (const attribute of attributes) {
            const nonEntity = attribute?.detailData?.nonEntityProfilePicture;
            const vectorImage = nonEntity?.vectorImage;
            const rootUrl = typeof vectorImage?.rootUrl === "string" ? vectorImage.rootUrl : "";
            const artifacts = Array.isArray(vectorImage?.artifacts) ? vectorImage.artifacts : [];
            const artifact = artifacts[artifacts.length - 1] || artifacts[0];
            const segment = artifact?.fileIdentifyingUrlPathSegment;

            if (typeof segment === "string" && segment.length) {
                if (segment.startsWith("http://") || segment.startsWith("https://")) return segment;
                return `${rootUrl}${segment}`;
            }
        }

        return null;
    };

    const parseMutualInsight = (entityResult: any) => {
        const insightItems = Array.isArray(entityResult?.insightsResolutionResults)
            ? entityResult.insightsResolutionResults
            : [];

        const simpleInsight = insightItems
            .map((item: any) => item?.simpleInsight)
            .find((item: any) => !!item && typeof item === "object");

        const text = getText(simpleInsight?.title);
        const attributes = Array.isArray(simpleInsight?.title?.attributesV2)
            ? simpleInsight.title.attributesV2
            : [];

        const mutualCountMatch = typeof text === "string"
            ? text.match(/and\s+(\d+)\s+other mutual connections/i)
            : null;
        const mutualCount = mutualCountMatch ? Number(mutualCountMatch[1]) : null;

        const mutualProfileLinks = attributes
            .map((attr: any) => attr?.detailData?.hyperlink)
            .filter((link: any) => typeof link === "string" && /linkedin\.com\/in\//i.test(link));

        const mutualConnectionSearchUrl = attributes
            .map((attr: any) => attr?.detailData?.hyperlink)
            .find((link: any) => typeof link === "string" && /search\/results\/people/i.test(link)) || null;

        return {
            text,
            mutualCount,
            mutualProfileLinks,
            mutualConnectionSearchUrl,
        };
    };

    const searchRoot = normalizedPayload?.data?.data?.searchDashClustersByAll
        || normalizedPayload?.data?.searchDashClustersByAll
        || normalizedPayload?.searchDashClustersByAll
        || null;
    if (!searchRoot) {
        return {
            connections: [],
            paging: null,
            metadata: null,
            hasMore: false,
            nextStart: null,
            debug: {
                hasSearchRoot: false,
                clustersCount: 0,
                includedCount: Array.isArray(normalizedPayload?.included)
                    ? normalizedPayload.included.length
                    : Array.isArray(normalizedPayload?.data?.included)
                        ? normalizedPayload.data.included.length
                        : 0,
                entityResultRefsCount: 0,
            },
        };
    }

    const included = Array.isArray(normalizedPayload?.included)
        ? normalizedPayload.included
        : Array.isArray(normalizedPayload?.data?.included)
            ? normalizedPayload.data.included
            : [];
    const includedByUrn = new Map(
        included
            .filter((item: any) => typeof item?.entityUrn === "string")
            .map((item: any) => [item.entityUrn, item])
    );
    const includedProfiles = included.filter((item: any) =>
        typeof item?.entityUrn === "string" && item.entityUrn.includes("urn:li:fsd_profile:")
    );
    const includedProfileByPublicId = new Map(
        includedProfiles
            .filter((item: any) => typeof item?.publicIdentifier === "string" && item.publicIdentifier.length)
            .map((item: any) => [item.publicIdentifier, item])
    );

    const entityResultRefs: string[] = [];
    const elements = Array.isArray(searchRoot?.elements) ? searchRoot.elements : [];
    elements.forEach((cluster: any) => {
        const items = Array.isArray(cluster?.items) ? cluster.items : [];
        items.forEach((entry: any) => {
            const item = entry?.item || {};
            const entityResultRef = item?.["*entityResult"]
                || item?.entityResult
                || item?.["*item"]
                || entry?.["*entityResult"]
                || null;
            if (typeof entityResultRef === "string") {
                entityResultRefs.push(entityResultRef);
            }
        });
    });

    const inferredEntityResults = entityResultRefs.length
        ? []
        : included.filter((item: any) => {
            const itemType = typeof item?.$type === "string" ? item.$type : "";
            return itemType.includes("EntityResultViewModel")
                || (typeof item?.navigationUrl === "string" && !!item?.title);
        });

    const connectionRecords: Array<{ entityResultRef: string | null; entityResult: any }> = entityResultRefs.length
        ? entityResultRefs.map((ref) => ({
            entityResultRef: ref,
            entityResult: includedByUrn.get(ref) || {},
        }))
        : inferredEntityResults.map((entityResult: any) => ({
            entityResultRef: typeof entityResult?.entityUrn === "string" ? entityResult.entityUrn : null,
            entityResult,
        }));

    const connections: ParsedMutualConnection[] = connectionRecords.map(({ entityResultRef, entityResult }: { entityResultRef: string | null; entityResult: any }, index: number) => {
        const profileUrnFromRef = parseProfileUrnFromEntityResultRef(entityResultRef);
        const profileUrnFromTracking = typeof entityResult?.trackingUrn === "string"
            && entityResult.trackingUrn.startsWith("urn:li:fsd_profile:")
            ? entityResult.trackingUrn
            : null;
        const navigationUrl = entityResult?.navigationUrl || null;
        const publicIdentifierFromUrl = parsePublicIdentifierFromUrl(navigationUrl);
        const profileUrn = profileUrnFromRef || profileUrnFromTracking;
        const profile: any = profileUrn
            ? includedByUrn.get(profileUrn)
            : publicIdentifierFromUrl
                ? includedProfileByPublicId.get(publicIdentifierFromUrl)
                : null;
        const insight = parseMutualInsight(entityResult);

        const firstName = profile?.firstName || null;
        const lastName = profile?.lastName || null;
        const fullName = getText(entityResult?.title) || [firstName, lastName].filter(Boolean).join(" ") || null;

        return {
            rank: index + 1,
            entityResultUrn: entityResultRef,
            profileUrn,
            profileId: parseProfileIdFromUrn(profileUrn),
            trackingUrn: entityResult?.trackingUrn || null,
            fullName,
            firstName,
            lastName,
            publicIdentifier: profile?.publicIdentifier || publicIdentifierFromUrl,
            linkedinUrl: navigationUrl,
            headline: getText(entityResult?.primarySubtitle),
            location: getText(entityResult?.secondarySubtitle),
            connectionDegreeText: entityResult?.badgeText?.accessibilityText || getText(entityResult?.badgeText),
            mutualInsight: insight.text,
            mutualConnectionsCount: insight.mutualCount,
            mutualProfileLinks: insight.mutualProfileLinks,
            mutualConnectionsSearchUrl: insight.mutualConnectionSearchUrl,
            imageUrl: extractImageUrl(entityResult),
            rawEntityResult: entityResult,
            rawProfile: profile || null,
        };
    });

    const paging = parseMutualConnectionsPaging(normalizedPayload);

    return {
        connections,
        paging: {
            start: paging.start,
            count: paging.count,
            total: paging.total,
        },
        metadata: {
            searchId: searchRoot?.metadata?.searchId || null,
            primaryResultType: searchRoot?.metadata?.primaryResultType || null,
            totalResultCount: Number.isFinite(Number(searchRoot?.metadata?.totalResultCount))
                ? Number(searchRoot.metadata.totalResultCount)
                : paging.total,
            paginationToken: searchRoot?.metadata?.paginationToken || null,
        },
        hasMore: paging.hasMore,
        nextStart: paging.nextStart,
        debug: {
            hasSearchRoot: true,
            clustersCount: elements.length,
            includedCount: included.length,
            entityResultRefsCount: entityResultRefs.length,
            inferredEntityResultsCount: inferredEntityResults.length,
        },
    };
};